import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { apiRequest, ApiError } from '../../api/client.js';
import { useAuth } from '../../auth/AuthProvider.js';
import { SaasTenantPicker } from './SaasTenantPicker.js';

interface BackupRow {
  readonly backupId: string;
  readonly saasTenantId: string;
  readonly backupType: string;
  readonly status: string;
  readonly sizeBytes: string | null;
  readonly createdAt: string;
}

const BACKUP_TYPES = ['SCHEDULED', 'ON_DEMAND', 'PRE_RESTORE'];
const STATUSES = ['QUEUED', 'RUNNING', 'COMPLETED', 'FAILED', 'RESTORING', 'RESTORED', 'RESTORE_FAILED'];
const PAGE = 25;

/**
 * M43 §27 Backup & Recovery Console (§19 Backup & Recovery). Operators execute
 * on-demand backups for a tenant and restore from a completed backup. Viewing
 * needs BACKUP_VIEW; execute needs BACKUP_EXECUTE; restore needs BACKUP_RESTORE.
 */
export function BackupRecoveryConsole(): ReactNode {
  const { token } = useAuth();
  const [rows, setRows] = useState<BackupRow[]>([]);
  const [total, setTotal] = useState(0);
  const [statusFilter, setStatusFilter] = useState('');
  const [page, setPage] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [reload, setReload] = useState(0);

  const [saasTenantId, setSaasTenantId] = useState('');
  const [backupType, setBackupType] = useState('ON_DEMAND');
  const [actionError, setActionError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (statusFilter) params.set('status', statusFilter);
    params.set('pageSize', String(PAGE));
    params.set('page', String(page + 1));
    apiRequest<{ data: BackupRow[]; total: number }>(`/v1/saas/backups?${params.toString()}`, { token })
      .then((r) => { if (alive) { setRows(r.data); setTotal(r.total); setError(null); } })
      .catch((e) => { if (alive) setError((e as Error).message); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [token, statusFilter, page, reload]);

  const execute = async (e: FormEvent): Promise<void> => {
    e.preventDefault();
    setActionError(null);
    setNotice(null);
    if (!saasTenantId.trim()) { setActionError('A tenant id is required.'); return; }
    try {
      await apiRequest('/v1/saas/backups', {
        method: 'POST',
        body: { saasTenantId: saasTenantId.trim(), backupType },
        token,
      });
      setNotice('Backup executed.');
      setPage(0);
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setActionError('You are not authorised to execute backups (BACKUP_EXECUTE required).');
      else setActionError((err as Error).message);
    }
  };

  const restore = async (row: BackupRow): Promise<void> => {
    setActionError(null);
    setNotice(null);
    try {
      await apiRequest('/v1/saas/backups/restore', {
        method: 'POST',
        body: { saasTenantId: row.saasTenantId, sourceBackupId: row.backupId },
        token,
      });
      setNotice('Restore executed.');
      setReload((n) => n + 1);
    } catch (err) {
      if (err instanceof ApiError && err.status === 403) setActionError('You are not authorised to restore backups (BACKUP_RESTORE required).');
      else setActionError((err as Error).message);
    }
  };

  return (
    <main aria-label="backup-recovery-console">
      <h1>Backup &amp; Recovery Console</h1>

      <section aria-label="backup-execute">
        <h2>Execute Backup</h2>
        <form aria-label="backup-form" onSubmit={(e) => void execute(e)}>
          <SaasTenantPicker label="Tenant" value={saasTenantId} onPick={setSaasTenantId} />
          <label>Type
            <select aria-label="field-backup-type" value={backupType} onChange={(e) => setBackupType(e.target.value)}>
              {BACKUP_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
          <button type="submit" aria-label="submit-backup">Execute</button>
        </form>
        {actionError && <p role="alert" aria-label="backup-action-error">{actionError}</p>}
        {notice && <p aria-label="backup-notice">{notice}</p>}
      </section>

      <section aria-label="backup-filters">
        <label>Status
          <select aria-label="filter-status" value={statusFilter} onChange={(e) => { setStatusFilter(e.target.value); setPage(0); }}>
            <option value="">All</option>
            {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
        </label>
      </section>

      {error && <p role="alert" aria-label="backup-error">{error}</p>}
      {loading ? <p>Loading…</p> : (
        <>
          <p aria-label="backup-total">{total} backup(s).</p>
          <table aria-label="backup-table">
            <thead><tr><th scope="col">Type</th><th scope="col">Status</th><th scope="col">Size</th><th scope="col">Actions</th></tr></thead>
            <tbody>
              {rows.length === 0 ? (
                <tr><td colSpan={4}>No backups.</td></tr>
              ) : rows.map((b) => (
                <tr key={b.backupId} data-backup={b.backupId}>
                  <td>{b.backupType}</td>
                  <td aria-label={`backup-status-${b.backupId}`}>{b.status}</td>
                  <td>{b.sizeBytes ?? '—'}</td>
                  <td><button type="button" aria-label={`restore-${b.backupId}`} onClick={() => void restore(b)}>Restore</button></td>
                </tr>
              ))}
            </tbody>
          </table>

          <nav aria-label="backup-pagination">
            <button type="button" aria-label="prev-page" disabled={page === 0} onClick={() => setPage((p) => Math.max(0, p - 1))}>Previous</button>
            <span aria-label="page-indicator">Page {page + 1} of {Math.max(1, Math.ceil(total / PAGE))}</span>
            <button type="button" aria-label="next-page" disabled={(page + 1) * PAGE >= total} onClick={() => setPage((p) => p + 1)}>Next</button>
          </nav>
        </>
      )}
    </main>
  );
}
